/**
 * DashboardV2 - Apple-Inspired Minimal Dashboard
 * 
 * Philosophy: One job per component. Generosity with whitespace.
 * Information hierarchy so obvious a child understands it.
 * 
 * Component: Hero > Stats > Quick Actions > Activity
 * Data: Parallel loads with skeleton screens
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Hero from './components/Hero';
import StatBrick from './components/StatBrick';
import ActionButton from './components/ActionButton';
import ActivityLine from './components/ActivityLine';
import LoadingSkeleton from './components/LoadingSkeleton';
import './dashboardv2.css';

export default function DashboardV2() {
  const navigate = useNavigate();
  const { userSettings } = useAuth();

  // State
  const [currentTrip, setCurrentTrip] = useState(null);
  const [stats, setStats] = useState(null);
  const [activity, setActivity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load data in parallel
  useEffect(() => {
    if (!userSettings?.employee_id) {
      setLoading(false);
      setError('No employee profile found. Open Settings to finish setup.');
      return;
    }

    const loadDashboard = async () => {
      try {
        setLoading(true);
        const employeeId = userSettings.employee_id;

        // Parallel loads - all at once
        const [tripRes, statsRes, activityRes] = await Promise.all([
          fetch(`/api/v1/rotations/current?employee_id=${employeeId}`),
          fetch(`/api/v1/dashboard/stats?employee_id=${employeeId}`),
          fetch(`/api/v1/dashboard/activity?employee_id=${employeeId}`),
        ]);

        if (!tripRes.ok || !statsRes.ok || !activityRes.ok) {
          throw new Error('One or more dashboard requests failed');
        }

        const tripData = await tripRes.json();
        const statsData = await statsRes.json();
        const activityData = await activityRes.json();

        setCurrentTrip(tripData);
        setStats(statsData);
        setActivity(activityData);
        setError(null);
      } catch (err) {
        setError('Failed to load dashboard');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    loadDashboard();
  }, [userSettings?.employee_id]);

  // Skeleton loading
  if (loading) {
    return (
      <div className="dashboardv2">
        <div className="dashboardv2__container">
          <LoadingSkeleton />
        </div>
      </div>
    );
  }

  return (
    <div className="dashboardv2">
      <div className="dashboardv2__container">
        {/* HERO SECTION - Current Mission */}
        {currentTrip ? (
          <div className="dashboardv2__hero-section">
            <Hero trip={currentTrip} />
          </div>
        ) : (
          <div className="dashboardv2__no-trip">
            <div className="no-trip__icon">✈️</div>
            <h2>No Active Trip</h2>
            <p>Schedule one when ready</p>
          </div>
        )}

        {/* STATS SECTION - Three Key Numbers */}
        {stats && (
          <div className="dashboardv2__stats-section">
            <StatBrick
              value={stats.thisRotationCredit}
              label="This Rotation"
              unit="hrs"
              icon="📋"
            />
            <StatBrick
              value={stats.thisMonthRotations}
              label="This Month"
              unit="trips"
              icon="📍"
            />
            <StatBrick
              value={stats.ytdHours}
              label="YTD 2026"
              unit="hrs"
              icon="📊"
            />
          </div>
        )}

        {/* QUICK ACTIONS - Full Width Buttons */}
        <div className="dashboardv2__actions-section">
          <ActionButton
            icon="📁"
            label="View Rotations"
            onClick={() => navigate('/data-management?tab=rotations')}
          />
          <ActionButton
            icon="📄"
            label="View Timecards"
            onClick={() => navigate('/data-management?tab=timecards')}
          />
          <ActionButton
            icon="➕"
            label="Upload Document"
            onClick={() => navigate('/data-management?action=upload')}
          />
          <ActionButton
            icon="⚙️"
            label="Settings"
            onClick={() => navigate('/settings')}
          />
        </div>

        {/* ACTIVITY SECTION - Recent Actions */}
        {activity && activity.length > 0 && (
          <div className="dashboardv2__activity-section">
            <h3 className="activity-title">Recent Activity</h3>
            <div className="activity-list">
              {activity.slice(0, 5).map((item, idx) => (
                <ActivityLine key={idx} item={item} />
              ))}
            </div>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="dashboardv2__error">
            <p>{error}</p>
          </div>
        )}
      </div>
    </div>
  );
}
